import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { Heading, Page, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import LeasesHeaderNav from './LeasesHeaderNav';
import { FULLSTORY_EXCLUDE } from '../constants/analytics';
import { useIsInMobileWebView } from '../utils/mobileHandoffFunctions';
import { usePropertyInfoQuery } from '../utils/queries';
import { Property } from '../models';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';

type Props = PropsWithChildren;

export function PropertyDetails({ children }: Props) {
  const { propertyId } = useLeasingUrlParams();
  const isInMobileWebView = useIsInMobileWebView();

  const { data: propertyInfo } = usePropertyInfoQuery(propertyId, !!propertyId);

  const property = useMemo(() => {
    if (!propertyInfo) return undefined;
    return new Property({
      ...propertyInfo,
      propertyId,
    });
  }, [propertyInfo, propertyId]);

  return (
    <LeasesHeaderNav>
      <Page.Root css={{ background: 'bg.neutral' }}>
        {!isInMobileWebView && (
          <Page.Breadcrumb>
            <TextButton
              className={`${FULLSTORY_EXCLUDE}`}
              icon={<IconChevronLeftFilled />}
              iconPosition="start"
              onClick={() => {
                window.location.href = Property.getPropertyDashboardOverviewPath(property.listingAlias);
              }}
            >
              {propertyInfo?.listingAlias
                ? `${property?.streetAddress}, ${property?.city}, ${property?.state} ${property?.zip}`
                : ''}
            </TextButton>
          </Page.Breadcrumb>
        )}
        {!isInMobileWebView && (
          <Page.Header>
            <Heading level={1}>Leases</Heading>
          </Page.Header>
        )}
        <Page.Content>{children}</Page.Content>
      </Page.Root>
    </LeasesHeaderNav>
  );
}

export default PropertyDetails;
